import { Link } from 'react-router-dom';
import { Phone, Mail, Car, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import {
  Card,
  CardContent,
  UserAvatar,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui';
import { cn } from '@/lib/utils';

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  vehicleCount?: number;
  status?: 'active' | 'inactive';
  createdAt?: string;
}

interface CustomerCardProps {
  customer: Customer;
  onEdit?: (customer: Customer) => void;
  onDelete?: (customer: Customer) => void;
  compact?: boolean;
}

export function CustomerCard({
  customer,
  onEdit,
  onDelete,
  compact = false,
}: CustomerCardProps) {
  return (
    <Card
      className={cn(
        'transition-all duration-200 hover:shadow-md hover:border-primary/20',
        compact && 'p-3'
      )}
    >
      <CardContent className={cn('p-4', compact && 'p-0')}>
        <div className="flex items-start justify-between gap-4">
          <Link
            to={`/customers/${customer.id}`}
            className="flex items-center gap-3 flex-1 min-w-0"
          >
            <UserAvatar name={customer.name} size={compact ? 'sm' : 'md'} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium truncate">{customer.name}</p>
                {customer.status && (
                  <Badge
                    variant={customer.status === 'active' ? 'default' : 'secondary'}
                    className="text-xs"
                  >
                    {customer.status === 'active' ? 'Activo' : 'Inactivo'}
                  </Badge>
                )}
              </div>
              {!compact && (
                <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    <span className="truncate">{customer.email}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    {customer.phone}
                  </span>
                  {customer.vehicleCount !== undefined && (
                    <span className="flex items-center gap-1">
                      <Car className="h-3.5 w-3.5" />
                      {customer.vehicleCount} vehiculo{customer.vehicleCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              )}
            </div>
          </Link>

          {(onEdit || onDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Acciones</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(customer)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem
                    onClick={() => onDelete(customer)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Loading skeleton
export function CustomerCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 animate-pulse rounded bg-muted" />
            <div className="h-3 w-48 animate-pulse rounded bg-muted" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
